import React, { useEffect, useState } from 'react';
import { MODE, hasContentSequence, TextReportView, ModeToggleButton } from './useTextReport';
import type { TextReport } from './useTextReport';

interface SRDisplaySet extends AppTypes.DisplaySet {
  isImagingMeasurementReport?: boolean;
  measurements?: unknown[] | null;
  load: () => Promise<void>;
}

const UPSTREAM_SR_MODULE = '@ohif/extension-cornerstone-dicom-sr.viewportModule.dicom-sr';

export default function SRViewportWrapper(props: withAppTypes) {
  const displaySets = props.displaySets as SRDisplaySet[];
  const extensionManager = props.extensionManager as AppTypes.ExtensionManager;

  if (displaySets.length > 1) {
    throw new Error('SR viewport should only have a single display set');
  }

  const srDisplaySet = displaySets[0];
  const instance: TextReport | undefined = srDisplaySet.instance ?? srDisplaySet.instances?.[0];

  const isSycaiReport =
    instance?.Manufacturer === 'SYCAI' ||
    Object.keys(instance ?? {}).some(key => key.startsWith('1127'));

  const [mode, setMode] = useState(MODE.REPORT);

  const [loadedDisplaySet, setLoadedDisplaySet] = useState<SRDisplaySet | null>(null);
  const [failedDisplaySet, setFailedDisplaySet] = useState<SRDisplaySet | null>(null);

  useEffect(() => {
    if (!isSycaiReport || !srDisplaySet.isImagingMeasurementReport) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!srDisplaySet.isLoaded) {
          await srDisplaySet.load();
        }
        if (!cancelled) {
          setLoadedDisplaySet(srDisplaySet);
          setFailedDisplaySet(null);
        }
      } catch (error) {
        console.error('[sycai] SR display set failed to load', error);
        if (!cancelled) {
          setFailedDisplaySet(srDisplaySet);
          setLoadedDisplaySet(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [srDisplaySet, isSycaiReport]);

  if (!isSycaiReport) {
    const moduleEntry = extensionManager.getModuleEntry(UPSTREAM_SR_MODULE) as
      | { component: React.ComponentType<withAppTypes> }
      | undefined;

    if (!moduleEntry) {
      console.error(`[sycai] no module entry registered at ${UPSTREAM_SR_MODULE}`);
      return null;
    }

    const UpstreamSRViewport = moduleEntry.component;
    return <UpstreamSRViewport {...props} />;
  }

  if (!hasContentSequence(instance)) {
    return <TextReportView instance={instance} />;
  }

  if (failedDisplaySet === srDisplaySet) {
    return <TextReportView instance={instance} />;
  }

  const dataLoaded = !srDisplaySet.isImagingMeasurementReport || loadedDisplaySet === srDisplaySet;
  if (!dataLoaded) {
    return null;
  }

  if (srDisplaySet.isImagingMeasurementReport && srDisplaySet.measurements?.length === 0) {
    return <TextReportView instance={instance} />;
  }

  let content: React.ReactNode;
  if (mode === MODE.INFO) {
    content = <TextReportView instance={instance} />;
  } else {
    const moduleEntry = extensionManager.getModuleEntry(UPSTREAM_SR_MODULE) as
      | { component: React.ComponentType<withAppTypes> }
      | undefined;

    if (!moduleEntry) {
      console.error(`[sycai] no module entry registered at ${UPSTREAM_SR_MODULE}`);
      content = null;
    } else {
      const UpstreamSRViewport = moduleEntry.component;
      content = <UpstreamSRViewport {...props} />;
    }
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex justify-center py-1">
        <ModeToggleButton
          currentMode={mode}
          onModeChange={setMode}
        />
      </div>
      <div className="min-h-0 flex-1">{content}</div>
    </div>
  );
}
